import React from 'react';
import { X } from 'lucide-react';
import { addObjective, updateObjective, removeObjective } from './objectiveHelpers';

interface UploadForm {
  title: string;
  description: string;
  subject: 'math' | 'reading' | 'science' | 'general';
  targetLevel: string;
  objectives: string[];
}

interface UploadTargetModalProps {
  show: boolean;
  form: UploadForm;
  onFormChange: (form: UploadForm) => void;
  onSubmit: (e: React.FormEvent) => void;
  onClose: () => void;
}

export function UploadTargetModal({
  show,
  form,
  onFormChange,
  onSubmit,
  onClose
}: UploadTargetModalProps) {
  if (!show) return null;

  const updateField = (field: keyof UploadForm, value: any) => {
    onFormChange({ ...form, [field]: value });
  };

  return (
    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl p-6 max-w-md w-full">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Add Curriculum Target</h3>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label htmlFor="upload-title" className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              id="upload-title"
              type="text"
              value={form.title}
              onChange={(e) => updateField('title', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., Year 2 Addition and Subtraction"
              required
            />
          </div>

          <div>
            <label htmlFor="upload-subject" className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
            <select
              id="upload-subject"
              value={form.subject}
              onChange={(e) => updateField('subject', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="math">Math</option>
              <option value="reading">Reading</option>
              <option value="science">Science</option>
              <option value="general">General</option>
            </select>
          </div>

          <div>
            <label htmlFor="upload-target-level" className="block text-sm font-medium text-gray-700 mb-1">Target Level</label>
            <input
              id="upload-target-level"
              type="text"
              value={form.targetLevel}
              onChange={(e) => updateField('targetLevel', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., Year 2, Grade 1, Key Stage 1"
            />
          </div>

          <fieldset>
            <legend className="block text-sm font-medium text-gray-700 mb-1">Learning Objectives</legend>
            {form.objectives.map((objective, index) => (
              <div key={`upload-objective-${index}`} className="flex items-center space-x-2 mb-2">
                <input
                  type="text"
                  id={`upload-objective-${index}`}
                  value={objective}
                  onChange={(e) => updateObjective(index, e.target.value, form.objectives, (objs) => updateField('objectives', objs))}
                  placeholder={`Objective ${index + 1}`}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {form.objectives.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeObjective(index, form.objectives, (objs) => updateField('objectives', objs))}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={() => addObjective(form.objectives, (objs) => updateField('objectives', objs))}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              + Add objective
            </button>
          </fieldset>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Save Target
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
